import { useEffect, useRef, useState } from "react";
import { Download, DatabaseBackup, Upload, Loader2, ShieldCheck, HardDriveDownload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (s) => {
  try { return new Date(s).toLocaleString("tr-TR"); } catch { return s; }
};
const fmtSize = (b) => (b ? `${(b / 1024).toFixed(0)} KB` : "-");
const totalCount = (c) => Object.values(c || {}).reduce((a, b) => a + b, 0);

export default function AdminBackup() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const importRef = useRef();

  const load = () => {
    setLoading(true);
    api.get("/admin/backups").then((r) => setBackups(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const blobDownload = (data, name) => {
    const url = window.URL.createObjectURL(new Blob([data], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportNow = async () => {
    setBusy("export");
    try {
      const res = await api.get("/admin/backup/export", { responseType: "blob" });
      blobDownload(res.data, `orse-yedek-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Yedek indirildi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const snapshot = async () => {
    setBusy("snapshot");
    try {
      await api.post("/admin/backup/snapshot");
      toast.success("Sunucuda yedek oluşturuldu");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const downloadServer = async (id, created) => {
    try {
      const res = await api.get(`/admin/backups/${id}/download`, { responseType: "blob" });
      blobDownload(res.data, `orse-yedek-${String(created).slice(0, 10)}.json`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Bu yedekteki veriler mevcut verinin üzerine geri yüklenecek (aynı ID'ler güncellenir, eksikler eklenir). Devam edilsin mi?")) {
      if (importRef.current) importRef.current.value = "";
      return;
    }
    setBusy("import");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/backup/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = res.data.restored || {};
      toast.success(`Geri yüklendi: ${Object.entries(r).map(([k, v]) => `${k} ${v}`).join(", ")}`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Geri yükleme başarısız"); }
    finally { setBusy(""); if (importRef.current) importRef.current.value = ""; }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <DatabaseBackup className="text-gold" size={26} />
        <h1 className="font-display text-3xl text-foreground">Yedekleme</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Tüm verilerin (ürün, kategori, müşteri, sipariş, bakiye) yedeği. Her gece otomatik yedek alınır ve e-posta ile bildirilir.</p>

      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        <button data-testid="backup-export-button" onClick={exportNow} disabled={busy === "export"}
          className="flex items-center justify-center gap-2 h-12 rounded-lg bg-gold text-obsidian font-semibold hover:bg-gold-hover transition-colors disabled:opacity-60">
          {busy === "export" ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} Yedeği İndir (JSON)
        </button>
        <button data-testid="backup-snapshot-button" onClick={snapshot} disabled={busy === "snapshot"}
          className="flex items-center justify-center gap-2 h-12 rounded-lg border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-60">
          {busy === "snapshot" ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />} Sunucuda Yedek Oluştur
        </button>
        <button data-testid="backup-import-button" onClick={() => importRef.current?.click()} disabled={busy === "import"}
          className="flex items-center justify-center gap-2 h-12 rounded-lg border border-border text-foreground hover:border-gold hover:text-gold transition-colors disabled:opacity-60">
          {busy === "import" ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />} Yedekten Geri Yükle
        </button>
        <input ref={importRef} type="file" accept="application/json,.json" className="hidden" onChange={onImport} data-testid="backup-import-input" />
      </div>

      <h2 className="text-lg font-medium text-foreground mb-3">Sunucudaki Yedekler</h2>
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gold" size={24} /></div>
        ) : backups.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">Henüz sunucu yedeği yok. "Sunucuda Yedek Oluştur" ile ilk yedeği alabilirsiniz.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr>
                <th className="px-4 py-3 font-medium">Tarih</th>
                <th className="px-4 py-3 font-medium">Tür</th>
                <th className="px-4 py-3 font-medium">Kayıt</th>
                <th className="px-4 py-3 font-medium">Boyut</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.id} className="border-b border-border/50" data-testid={`backup-row-${b.id}`}>
                  <td className="px-4 py-3 text-foreground">{fmtDate(b.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{b.source === "auto" ? "Otomatik" : "Manuel"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{totalCount(b.counts)} kayıt</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtSize(b.size)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => downloadServer(b.id, b.created_at)} data-testid={`backup-download-${b.id}`}
                      className="inline-flex items-center gap-1 text-gold hover:underline">
                      <HardDriveDownload size={15} /> İndir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
